import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { PrimaryButton } from "../../components/Buttons.jsx";
import Loader from "../../components/Loader.jsx";
import CanView from "@/components/CanView.jsx";
import SoundSelector from "../../components/SoundSelector.jsx";
import { loadNotificationSettings, saveNotificationSettings, forceSyncToServer, wipeNotificationSettings } from "../../utils/appNotifications.js";
import { addToast } from "@/store/app/action.js";


function AppNotificationPanel() {
  const dispatch = useDispatch();
  const { merchants, user } = useSelector((state) => state.app);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wiping, setWiping] = useState(false);
  
  // State to track notification settings for each location
  const [notificationSettings, setNotificationSettings] = useState({});
  
  // State for notification sound preferences
  const [selectedSound, setSelectedSound] = useState("Gentle Ping.aiff");
  const [selectedSoundOrderUpdated, setSelectedSoundOrderUpdated] = useState("Gentle Ping.aiff");
  const [selectedSoundOrderCanceled, setSelectedSoundOrderCanceled] = useState("Gentle Ping.aiff");

  // Handle sound selection change
  const handleSoundChange = (soundType, filename) => {
    switch (soundType) {
      case "newOrder":
        setSelectedSound(filename);
        break;
      case "orderUpdated":
        setSelectedSoundOrderUpdated(filename);
        break;
      case "orderCanceled":
        setSelectedSoundOrderCanceled(filename);
        break;
      default:
        setSelectedSound(filename);
    }
  };

  // Load notification settings when component mounts
  React.useEffect(() => {
    if (user?.id) {
      const loadSettings = async () => {
        setLoading(true);
        try {
          const settings = await loadNotificationSettings(user.id);
          const { sound, soundOrderUpdated, soundOrderCanceled, ...locationSettings } = settings;
          
          setNotificationSettings(locationSettings);
          
          // Load sound preferences if available
          if (sound) {
            setSelectedSound(sound);
          }
          if (soundOrderUpdated) {
            setSelectedSoundOrderUpdated(soundOrderUpdated);
          }
          if (soundOrderCanceled) {
            setSelectedSoundOrderCanceled(soundOrderCanceled);
          }
        } catch (error) {
          // Silent error handling
        } finally {
          setLoading(false);
        }
      };
      
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const handleToggleChange = async (locationId, enabled) => {
    const updatedSettings = {
      ...notificationSettings,
      [locationId]: {
        ...notificationSettings[locationId],
        notification: enabled
      }
    };
    
    setNotificationSettings(updatedSettings);
    
    // Auto-save to Capacitor Preferences
    try {
      await saveNotificationSettings(user.id, updatedSettings);
    } catch (error) {
      // Revert the change if save failed
      setNotificationSettings(notificationSettings);
      dispatch(addToast({ type: "error", message: "Failed to save change", timer: 3 }));
    }
  };

  const handleSave = async () => {
    setProcessing(true);
    try {
      // Combine location settings with sound preferences for local storage
      const settingsWithSound = {
        ...notificationSettings,
        sound: selectedSound,
        soundOrderUpdated: selectedSoundOrderUpdated,
        soundOrderCanceled: selectedSoundOrderCanceled
      };
      
      // Save to local storage
      await saveNotificationSettings(user.id, settingsWithSound);
      
      // Force a sync to ensure server is up to date, passing all sounds
      await forceSyncToServer(
        user.id, 
        notificationSettings, 
        null, 
        selectedSound,
        selectedSoundOrderUpdated,
        selectedSoundOrderCanceled
      );
      
      dispatch(addToast({
        type: "success",
        message: "Notification settings saved",
        timer: 3,
      }));
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: "Failed to save notification settings",
        timer: 5,
      }));
    } finally {
      setProcessing(false);
    }
  };

  const handleWipeSettings = async () => {
    if (!window.confirm('Are you sure you want to wipe all notification settings? This action cannot be undone.')) {
      return;
    }

    setWiping(true);
    try {
      await wipeNotificationSettings(user.id);
      // Reset local state to default (all enabled)
      const defaultSettings = {};
      merchants.forEach((merchant) => {
        merchant.locations?.forEach((location) => {
          defaultSettings[location.id] = {
            notification: true,
            store_id: location.store_id
          };
        });
      });
      setNotificationSettings(defaultSettings);
      
      dispatch(addToast({
        type: "success",
        message: "Notification settings wiped",
        timer: 3,
      }));
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: "Failed to wipe notification settings",
        timer: 5,
      }));
    } finally {
      setWiping(false);
    }
  };

  if (loading) {
    return (
      <div className="grow">
        <Loader />
      </div>
    );
  }

  if (!merchants || merchants.length === 0) {
    return (
      <div className="grow">
        <div className="p-6">
          <div className="text-center text-slate-500 dark:text-slate-400">
            No merchants found
          </div>
        </div>
      </div>
    );
  }

  if (!user?.id) {
    return (
      <div className="grow">
        <div className="p-6">
          <div className="text-center text-slate-500 dark:text-slate-400">
            Please log in to configure notification settings
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grow">
        <>
          <div className="p-6 space-y-6">
            <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
              In-App Notifications
            </h2>
            
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Configure which stores you want to receive in-app notifications for when new orders arrive.
            </div>

            {/* Notification Sound Selection */}
            <div className="mb-8 space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Order Notification Tones
              </h3>
              <SoundSelector
                selectedSound={selectedSound}
                onSoundChange={(filename) => handleSoundChange("newOrder", filename)}
                defaultSound="Gentle Ping.aiff"
                description="Select a notification tone for when new orders arrive."
                title="New Order"
              />
              <SoundSelector
                selectedSound={selectedSoundOrderUpdated}
                onSoundChange={(filename) => handleSoundChange("orderUpdated", filename)}
                defaultSound="Gentle Ping.aiff"
                description="Select a notification tone for when orders are updated."
                title="Order Updated"
              />
              <SoundSelector
                selectedSound={selectedSoundOrderCanceled}
                onSoundChange={(filename) => handleSoundChange("orderCanceled", filename)}
                defaultSound="Gentle Ping.aiff"
                description="Select a notification tone for when orders are canceled."
                title="Order Canceled"
              />
            </div>

            {/* Merchants and Locations */}
            <div className="space-y-8">
              {merchants.map((merchant) => (
                <div key={merchant.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                  {/* Merchant Header */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                      {merchant.name}
                    </h3>
                  </div>

                  {/* Locations */}
                  {merchant.locations && merchant.locations.length > 0 ? (
                    <div className="space-y-4">
                      {merchant.locations.map((location) => {
                        const setting = notificationSettings[location.id];
                        
                        return (
                          <div 
                            key={location.id}
                            className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-slate-800 dark:text-slate-100">
                                {location.name}
                              </div>
                              <div className="text-sm text-slate-600 dark:text-slate-400">
                                Store ID: {location.store_id}
                              </div>
                              {location.address && (
                                <div className="text-sm text-slate-500 dark:text-slate-500">
                                  {location.address}, {location.city}, {location.state} {location.zip}
                                </div>
                              )}
                            </div>
                            
                            <div className="ml-4">
                              <ToggleSwitch
                                checked={setting?.notification ?? false}
                                onChange={(enabled) => handleToggleChange(location.id, enabled)}
                                onLabel="Enabled"
                                offLabel="Disabled"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 dark:text-slate-400 py-8">
                      No locations found for this merchant
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Panel footer */}
          <footer>
            <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
              <div className="flex self-end space-x-4">
                  <button
                    onClick={handleWipeSettings}
                    disabled={wiping}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {wiping ? 'Wiping...' : 'Wipe Settings (Dev Only)'}
                  </button>
                
                <div className="flex space-x-4">
                  <PrimaryButton onClick={handleSave} loading={processing}>
                    Save Changes
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </footer>
        </>
    </div>
  );
}

export default AppNotificationPanel;
